import { Link } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import SubtleHint from '@/components/SubtleHint';
import { useExperienceMode } from '@/contexts/ExperienceModeContext';
import AlternateModeToggle from '@/components/AlternateModeToggle';
import AmbientMusicControl from '@/components/AmbientMusicControl';

const Index = () => {
  const [visible, setVisible] = useState(false);
  const [taglineVisible, setTaglineVisible] = useState(false);
  const [enterVisible, setEnterVisible] = useState(false);
  const [titleGlitch, setTitleGlitch] = useState(false);
  const { t } = useLanguage();
  const { isAlternate, isDiscovered, getModeIntensity } = useExperienceMode();

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), isAlternate ? 500 : 300);
    const t2 = setTimeout(() => setTaglineVisible(true), isAlternate ? 1600 : 1200);
    const t3 = setTimeout(() => setEnterVisible(true), isAlternate ? 2800 : 2200);
    
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [isAlternate]);

  // Random title glitch in alternate mode
  useEffect(() => {
    if (!isAlternate) return;
    
    const glitchInterval = setInterval(() => {
      if (Math.random() < 0.1) {
        setTitleGlitch(true);
        setTimeout(() => setTitleGlitch(false), 100);
      }
    }, 4000);
    
    return () => clearInterval(glitchInterval);
  }, [isAlternate]);

  const hints = useMemo(() => {
    if (isAlternate) {
      return [
        "Something is different",
        "The doors have shifted",
        "Reality bends here",
        "Not everything is as it was"
      ];
    }
    return [
      "Click 'Enter' to begin",
      "Take your time",
      "Each room holds a question",
      "Press any key to start"
    ];
  }, [isAlternate]);

  const intensity = getModeIntensity();

  return (
    <div 
      className={`min-h-screen flex flex-col items-center justify-center p-8 bg-background relative transition-all duration-1000
        ${isAlternate ? 'alternate-index' : ''}
      `}
      style={{
        filter: isAlternate ? `hue-rotate(${intensity * 8}deg)` : 'none',
      }}
    >
      {/* Controls */}
      <div className="absolute top-6 right-6 flex items-center gap-2">
        <AmbientMusicControl category={isAlternate ? 'scary' : 'default'} />
        {isDiscovered && <AlternateModeToggle />}
        <LanguageSwitcher />
      </div>

      {/* Radial gradient background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-radial from-foreground/[0.03] to-transparent" />
      </div>

      <div className="max-w-2xl text-center space-y-16 relative">
        {/* Title - with alternate trigger + breathing animation */}
        <h1 
          data-alternate-trigger
          className={`text-3xl md:text-5xl font-light tracking-tight transition-all duration-1000 select-none animate-title-breathe
            ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
            ${titleGlitch ? 'translate-x-px text-destructive/80' : ''}
            ${isAlternate ? 'tracking-widest' : 'tracking-tight'}
          `}
          style={{
            textShadow: isAlternate 
              ? `0 0 ${20 * intensity}px hsl(var(--alternate-glow) / 0.3)` 
              : 'none',
          }}
        >
          {t('title')}
        </h1>

        {/* Tagline - mode-specific */}
        <p 
          className={`text-muted-foreground text-lg md:text-xl font-light leading-relaxed transition-all duration-1000 ${
            taglineVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {isAlternate ? t('alternate_tagline') : t('tagline')}
        </p>

        {/* Enter button - mode-specific styling */}
        <div 
          className={`transition-all duration-1000 ${
            enterVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <Link 
            to="/hallway" 
            className={`btn-minimal inline-block active:scale-95 transition-all
              ${isAlternate ? 'hover:tracking-widest hover:text-foreground/80' : ''}
            `}
            style={{
              borderColor: isAlternate ? 'hsl(var(--alternate-glow) / 0.3)' : undefined,
            }}
          >
            {isAlternate ? t('alternate_enter') : t('enter')}
          </Link>
        </div>

        {/* Subtle hint for new users */}
        <div 
          className={`h-6 transition-all duration-1000 delay-1000 ${
            enterVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <SubtleHint hints={hints} delay={isAlternate ? 4000 : 6000} />
        </div>

        {/* Footer - mode-specific */}
        <p 
          className={`text-xs text-muted-foreground transition-all duration-1000 delay-500 ${
            enterVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {isAlternate ? t('alternate_footer') : t('footer')}
        </p>
      </div>

      {/* Keyboard hint at bottom */}
      <div 
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 text-xs text-muted-foreground transition-all duration-1000 ${
          enterVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <span className="hidden md:inline">
          {isAlternate 
            ? 'Press H for hallway · reality shifts at random' 
            : 'Press H for hallway · ? for random room'
          }
        </span>
      </div>

      {/* Alternate mode ambient particles */}
      {isAlternate && enterVisible && (
        <div className="fixed inset-0 pointer-events-none z-10 overflow-hidden" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-foreground/5 rounded-full animate-float"
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + i * 10}%`,
                animationDelay: `${i * 1.5}s`,
                animationDuration: `${10 + i * 2}s`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Index;
